import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { XCircle, RefreshCw, Phone, Mail, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { reportError } from '../lib/errors';
import { DEFAULT_DOCUMENT_ACCENT } from '../lib/brandAccent';

interface InvoiceContact {
    invoice_id: string;
    invoice_number: string;
    status: string;
    total_gross: number;
    balance_due: number;
    garage_name: string;
    garage_branding: Record<string, any>;
    garage_phone: string | null;
    garage_email: string | null;
    org_id: string;
}

export default function PaymentCancelled() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const [invoice, setInvoice] = useState<InvoiceContact | null>(null);
    const [loading, setLoading] = useState(true);
    const [retrying, setRetrying] = useState(false);

    useEffect(() => {
        if (!token) {
            setLoading(false);
            return;
        }

        const fetchContact = async () => {
            try {
                // Payment redirects retain the same expiring, revocable capability.
                const { data: res, error: resError } = await supabase.functions.invoke(
                    `get-public-invoice?token=${encodeURIComponent(token)}`,
                    { method: 'GET' },
                );
                if (resError) reportError(resError, 'payment-cancelled.get-public-invoice');
                if (res?.data) setInvoice(res.data);
            } catch (err) {
                reportError(err, 'payment-cancelled.load-contact');
            } finally {
                setLoading(false);
            }
        };

        fetchContact();
    }, [token]);

    const handleRetryPayment = async () => {
        if (!token || !invoice || retrying) return;
        setRetrying(true);

        try {
            const { data, error } = await supabase.functions.invoke('create-invoice-checkout', {
                body: { invoice_id: invoice?.invoice_id, token },
            });

            if (error) throw error;
            if (data?.checkout_url) {
                window.location.href = data.checkout_url;
            }
        } catch (err) {
            console.error('Failed to create checkout session:', err);
            setRetrying(false);
        }
    };

    const accentColor = invoice?.garage_branding?.accent_color || DEFAULT_DOCUMENT_ACCENT;

    if (loading) {
        return (
            <div className="min-h-screen bg-panel flex items-center justify-center">
                <Loader2 className="w-10 h-10 animate-spin text-fg-4" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-panel flex items-center justify-center p-4 md:p-6">
            <div className="max-w-lg w-full">
                <div className="bg-tint/5 backdrop-blur-xl border border-tint/10 rounded-2xl overflow-hidden">

                    {/* Header */}
                    <div className="px-8 pt-10 pb-8 text-center">
                        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-danger/10 flex items-center justify-center">
                            <XCircle className="w-10 h-10 text-danger" />
                        </div>

                        <h1 className="text-2xl md:text-3xl font-bold text-fg mb-2">
                            Payment Cancelled
                        </h1>
                        <p className="text-fg-3 text-sm max-w-sm mx-auto">
                            No charge has been made to your card. Your invoice is still outstanding
                            {invoice?.invoice_number && (
                                <> — Invoice #{invoice.invoice_number}</>
                            )}
                            .
                        </p>
                    </div>

                    {/* Outstanding Amount */}
                    {invoice?.balance_due && Number(invoice.balance_due) > 0 && (
                        <div className="px-8 py-5 border-t border-tint/5">
                            <div className="flex items-center justify-between">
                                <span className="text-fg-3 text-sm">Balance Due</span>
                                <span className="text-xl font-bold font-mono tabular-nums text-fg">
                                    £{Number(invoice.balance_due).toFixed(2)}
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="px-8 py-6 border-t border-tint/5 space-y-3">
                        {/* Try Again */}
                        <button
                            onClick={handleRetryPayment}
                            disabled={retrying || !token || !invoice}
                            className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium text-on-fill transition-all duration-200 hover:opacity-90 disabled:opacity-50"
                            style={{ backgroundColor: accentColor }}
                        >
                            {retrying ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                                <RefreshCw className="w-5 h-5" />
                            )}
                            {retrying ? 'Loading...' : 'Try Again'}
                        </button>

                        {/* Contact Garage */}
                        {(invoice?.garage_phone || invoice?.garage_email) && (
                            <div className="pt-2">
                                <p className="text-fg-4 text-xs text-center mb-3">
                                    Or contact {invoice.garage_name || 'the garage'} directly:
                                </p>
                                <div className="flex gap-3">
                                    {invoice.garage_phone && (
                                        <a
                                            href={`tel:${invoice.garage_phone}`}
                                            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-tint/5 hover:bg-tint/10 border border-tint/10 text-fg rounded-xl text-sm font-medium transition-all duration-200"
                                        >
                                            <Phone className="w-4 h-4" />
                                            Call
                                        </a>
                                    )}
                                    {invoice.garage_email && (
                                        <a
                                            href={`mailto:${invoice.garage_email}`}
                                            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-tint/5 hover:bg-tint/10 border border-tint/10 text-fg rounded-xl text-sm font-medium transition-all duration-200"
                                        >
                                            <Mail className="w-4 h-4" />
                                            Email
                                        </a>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <p className="text-center text-fg-4 text-xs mt-6">
                    Powered by ForgeDash
                </p>
            </div>
        </div>
    );
}
